import { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Trans } from '@libs/i18n';

/**
 * Re-assessment legend explaining the green/red colour-coding on the
 * "Missing Documents" stat card and per-domain rows. Rendered only when
 * the page is showing a re-assessment (version > 1 with parent results).
 */
export interface MissingDocsLegendProps {
  parentVersion?: number;
}

export const MissingDocsLegend: FC<MissingDocsLegendProps> = observer(({ parentVersion }) => {
  const theme = useTheme();

  const Dot: FC<{ color: string }> = observer(({ color }) => (
    <Box
      component="span"
      sx={{
        width: 9,
        height: 9,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  ));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: 'fit-content',
      }}
    >
      <Typography
        variant="textXS_medium"
        sx={{
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '10px',
        }}
      >
        <Trans i18nKey="assessments.summary.missingDocsLegend.title" />
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Dot color={theme.palette.green[600]} />
          <Typography
            variant="textXS_regular"
            sx={{
              color: 'text.primary',
              fontSize: '11px',
            }}
          >
            <Trans
              i18nKey="assessments.summary.missingDocsLegend.better"
              values={{ version: parentVersion ?? '?' }}
            />
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Dot color={theme.palette.error.main} />
          <Typography
            variant="textXS_regular"
            sx={{
              color: 'text.primary',
              fontSize: '11px',
            }}
          >
            <Trans
              i18nKey="assessments.summary.missingDocsLegend.worse"
              values={{ version: parentVersion ?? '?' }}
            />
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});
